/**
 * LanguageTool Extension for TipTap
 * 
 * Adapted from: https://github.com/sereneinserenade/tiptap-languagetool
 * 
 * MIT License
 * Copyright (c) 2022 Jeet Mandaliya
 * 
 * This extension integrates LanguageTool spell/grammar checking with TipTap editor.
 */

import { Extension } from '@tiptap/core';
import { Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet, EditorView } from '@tiptap/pm/view';

// ============= TYPES =============

export interface Replacement {
    value: string;
}

export interface Context {
    text: string;
    offset: number;
    length: number;
}

export interface Category {
    id: string;
    name: string;
}

export interface Rule {
    id: string;
    description: string;
    issueType: string;
    category: Category;
}

export interface Match {
    message: string;
    shortMessage: string;
    replacements: Replacement[];
    offset: number;
    length: number;
    context: Context;
    sentence: string;
    type: { typeName: string };
    rule: Rule;
    ignoreForIncompleteSentence: boolean;
    contextForSureMatch: number;
}

export interface LanguageToolResponse {
    software: {
        name: string;
        version: string;
        buildDate: string;
        apiVersion: number;
        premium: boolean;
        status: string;
    };
    warnings: { incompleteResults: boolean };
    language: {
        name: string;
        code: string;
        detectedLanguage: {
            name: string;
            code: string;
            confidence: number;
        };
    };
    matches: Match[];
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        languagetool: {
            proofread: () => ReturnType;
            toggleProofreading: () => ReturnType;
            ignoreLanguageToolSuggestion: () => ReturnType;
            resetLanguageToolMatch: () => ReturnType;
            toggleLanguageTool: () => ReturnType;
            getLanguageToolState: () => ReturnType;
        };
    }
}

interface TextNodesWithPosition {
    text: string;
    from: number;
    to: number;
}

interface LanguageToolOptions {
    language: string;
    apiUrl: string;
    automaticMode: boolean;
    documentId?: string | number;
}

interface LanguageToolStorage {
    match?: Match;
    loading?: boolean;
    matchRange?: { from: number; to: number };
    active: boolean;
}

// ============= HELPERS =============

// Simple debounce implementation
function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    return (...args: Parameters<T>) => {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
}

// ============= EXTENSION STATE =============

let editorView: EditorView;
let decorationSet: DecorationSet;
let apiUrl = '';
let textNodesWithPosition: TextNodesWithPosition[] = [];
let match: Match | undefined = undefined;
let matchRange: { from: number; to: number } | undefined;
let proofReadInitially = false;
let isLanguageToolActive = true;
let ignoredWords: Set<string> = new Set();

export enum LanguageToolHelpingWords {
    LanguageToolTransactionName = 'languageToolTransaction',
    MatchUpdatedTransactionName = 'matchUpdated',
    MatchRangeUpdatedTransactionName = 'matchRangeUpdated',
    LoadingTransactionName = 'languageToolLoading',
}

const dispatch = (tr: Transaction) => editorView.dispatch(tr);

const updateMatchAndRange = (m?: Match, range?: { from: number; to: number }) => {
    if (m) match = m;
    else match = undefined;
    if (range) matchRange = range;
    else matchRange = undefined;

    const tr = editorView.state.tr;
    tr.setMeta(LanguageToolHelpingWords.MatchUpdatedTransactionName, true);
    tr.setMeta(LanguageToolHelpingWords.MatchRangeUpdatedTransactionName, true);
    editorView.dispatch(tr);
};

const mouseEventsListener = (e: Event) => {
    if (!e.target) return;

    // Find the closest .lt decoration element - the target might be a nested element
    // (e.g., if the text has bold/italic formatting inside the error span)
    const ltSpan = (e.target as HTMLElement).closest('span.lt');
    if (!ltSpan) return;

    const matchString = ltSpan.getAttribute('data-match')?.trim();
    if (!matchString) {
        return;
    }

    try {
        const { match: parsedMatch, from, to } = JSON.parse(matchString);
        updateMatchAndRange(parsedMatch, { from, to });
    } catch (err) {
        console.error('Error parsing match data:', err);
    }
};

const debouncedMouseEventsListener = debounce(mouseEventsListener, 50);

const addEventListenersToDecorations = () => {
    const decorations = document.querySelectorAll('span.lt');
    if (!decorations.length) return;

    decorations.forEach((el) => {
        // Remove existing listeners to avoid duplicates
        el.removeEventListener('mouseover', debouncedMouseEventsListener);
        el.removeEventListener('mouseenter', debouncedMouseEventsListener);
        el.removeEventListener('click', mouseEventsListener);
        
        // Add event listeners - both hover and click to trigger suggestions
        el.addEventListener('mouseover', debouncedMouseEventsListener);
        el.addEventListener('mouseenter', debouncedMouseEventsListener);
        el.addEventListener('click', mouseEventsListener);
    });
};

const gimmeDecoration = (from: number, to: number, match: Match) =>
    Decoration.inline(from, to, {
        class: `lt lt-${match.rule.issueType}`,
        nodeName: 'span',
        'data-match': JSON.stringify({ match, from, to }),
    });

const moreThan500Words = (s: string) => s.trim().split(/\s+/).length >= 500;

const getMatchAndSetDecorations = async (
    doc: PMNode,
    text: string,
    originalFrom: number
) => {
    if (!apiUrl || !text.trim()) return;

    try {
        const postOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                Accept: 'application/json',
            },
            body: `text=${encodeURIComponent(text)}&language=en-US&enabledOnly=false`,
        };

        const response = await fetch(apiUrl, postOptions);
        const ltRes: LanguageToolResponse = await response.json();
        const { matches } = ltRes;

        const decorations: Decoration[] = [];
        for (const m of matches) {
            const docFrom = m.offset + originalFrom;
            const docTo = docFrom + m.length;
            const content = text.substring(m.offset, m.offset + m.length);

            // Skip ignored words
            if (!ignoredWords.has(content.toLowerCase())) {
                decorations.push(gimmeDecoration(docFrom, docTo, m));
            }
        }

        const decorationsToRemove = decorationSet.find(
            originalFrom,
            originalFrom + text.length
        );
        decorationSet = decorationSet.remove(decorationsToRemove);
        decorationSet = decorationSet.add(doc, decorations);

        if (editorView) {
            dispatch(
                editorView.state.tr.setMeta(
                    LanguageToolHelpingWords.LanguageToolTransactionName,
                    true
                )
            );
        }

        setTimeout(addEventListenersToDecorations, 100);
    } catch (error) {
        console.error('LanguageTool API error:', error);
    }
};

const debouncedGetMatchAndSetDecorations = debounce(getMatchAndSetDecorations, 300);

let lastOriginalFrom = 0;

const onNodeChanged = (doc: PMNode, text: string, originalFrom: number) => {
    if (originalFrom !== lastOriginalFrom) getMatchAndSetDecorations(doc, text, originalFrom);
    else debouncedGetMatchAndSetDecorations(doc, text, originalFrom);
    lastOriginalFrom = originalFrom;
};

const proofreadAndDecorateWholeDoc = async (doc: PMNode, nodePos = 0) => {
    textNodesWithPosition = [];
    let index = 0;

    doc?.descendants((node, pos) => {
        if (!node.isText) {
            index += 1;
            return;
        }

        const intermediateTextNodeWithPos: TextNodesWithPosition = {
            text: '',
            from: -1,
            to: -1,
        };

        if (textNodesWithPosition[index]) {
            intermediateTextNodeWithPos.text =
                textNodesWithPosition[index].text + (node.text || '');
            intermediateTextNodeWithPos.from =
                textNodesWithPosition[index].from + nodePos;
            intermediateTextNodeWithPos.to =
                intermediateTextNodeWithPos.from +
                intermediateTextNodeWithPos.text.length +
                nodePos;
        } else {
            intermediateTextNodeWithPos.text = node.text || '';
            intermediateTextNodeWithPos.from = pos + nodePos;
            intermediateTextNodeWithPos.to = pos + nodePos + (node.text?.length || 0);
        }

        textNodesWithPosition[index] = intermediateTextNodeWithPos;
    });

    textNodesWithPosition = textNodesWithPosition.filter(Boolean);

    let finalText = '';
    const chunksOf500Words: { from: number; text: string }[] = [];
    let upperFrom = 0 + nodePos;
    let newDataSet = true;
    let lastPos = 1 + nodePos;

    for (const { text, from, to } of textNodesWithPosition) {
        if (!newDataSet) {
            upperFrom = from;
            newDataSet = true;
        } else {
            const diff = from - lastPos;
            if (diff > 0) finalText += Array(diff + 1).join(' ');
        }

        lastPos = to;
        finalText += text;

        if (moreThan500Words(finalText)) {
            const updatedFrom = chunksOf500Words.length ? upperFrom : upperFrom + 1;
            chunksOf500Words.push({ from: updatedFrom, text: finalText });
            finalText = '';
            newDataSet = false;
        }
    }

    chunksOf500Words.push({
        from: chunksOf500Words.length ? upperFrom : 1,
        text: finalText,
    });

    const requests = chunksOf500Words.map(({ text, from }) =>
        getMatchAndSetDecorations(doc, text, from)
    );

    if (editorView) {
        dispatch(
            editorView.state.tr.setMeta(
                LanguageToolHelpingWords.LoadingTransactionName,
                true
            )
        );
    }

    Promise.all(requests).then(() => {
        if (editorView) {
            dispatch(
                editorView.state.tr.setMeta(
                    LanguageToolHelpingWords.LoadingTransactionName,
                    false
                )
            );
        }
    });

    proofReadInitially = true;
};

const debouncedProofreadAndDecorate = debounce(proofreadAndDecorateWholeDoc, 500);

// ============= EXTENSION =============

export const LanguageTool = Extension.create<LanguageToolOptions, LanguageToolStorage>({
    name: 'languagetool',

    addOptions() {
        return {
            language: 'auto',
            apiUrl: 'https://api.languagetool.org/v2/check',
            automaticMode: true,
            documentId: undefined,
        };
    },

    addStorage() {
        return {
            match: match,
            loading: false,
            matchRange: { from: -1, to: -1 },
            active: isLanguageToolActive,
        };
    },

    addCommands() {
        return {
            proofread:
                () =>
                ({ tr }) => {
                    apiUrl = this.options.apiUrl;
                    proofreadAndDecorateWholeDoc(tr.doc);
                    return true;
                },

            ignoreLanguageToolSuggestion:
                () =>
                ({ editor }) => {
                    const { selection, doc } = editor.state;
                    const { from, to } = selection;

                    decorationSet = decorationSet.remove(
                        decorationSet.find(from, to)
                    );

                    const content = doc.textBetween(from, to);
                    ignoredWords.add(content.toLowerCase());

                    return false;
                },

            resetLanguageToolMatch:
                () =>
                ({
                    editor: {
                        view: {
                            dispatch,
                            state: { tr },
                        },
                    },
                }) => {
                    match = undefined;
                    matchRange = undefined;
                    dispatch(
                        tr
                            .setMeta(
                                LanguageToolHelpingWords.MatchRangeUpdatedTransactionName,
                                true
                            )
                            .setMeta(
                                LanguageToolHelpingWords.MatchUpdatedTransactionName,
                                true
                            )
                    );
                    return false;
                },

            toggleLanguageTool:
                () =>
                ({ commands }) => {
                    isLanguageToolActive = !isLanguageToolActive;
                    if (isLanguageToolActive) commands.proofread();
                    else commands.resetLanguageToolMatch();
                    this.storage.active = isLanguageToolActive;
                    return false;
                },

            getLanguageToolState: () => () => isLanguageToolActive,
        };
    },

    addProseMirrorPlugins() {
        const { apiUrl: optionsApiUrl } = this.options;
        apiUrl = optionsApiUrl;

        const extensionThis = this;

        return [
            new Plugin({
                key: new PluginKey('languagetoolPlugin'),
                props: {
                    decorations(state) {
                        return this.getState(state);
                    },
                    attributes: {
                        spellcheck: 'false',
                    },
                    handlePaste(view) {
                        const { docChanged } = view.state.tr;
                        if (docChanged) debouncedProofreadAndDecorate(view.state.tr.doc);
                        return false;
                    },
                },
                state: {
                    init: (_, state) => {
                        decorationSet = DecorationSet.create(state.doc, []);
                        if (extensionThis.options.automaticMode) {
                            proofreadAndDecorateWholeDoc(state.doc);
                        }
                        return decorationSet;
                    },
                    apply: (tr) => {
                        if (!isLanguageToolActive) return DecorationSet.empty;

                        const matchUpdated = tr.getMeta(
                            LanguageToolHelpingWords.MatchUpdatedTransactionName
                        );
                        const matchRangeUpdated = tr.getMeta(
                            LanguageToolHelpingWords.MatchRangeUpdatedTransactionName
                        );
                        const loading = tr.getMeta(
                            LanguageToolHelpingWords.LoadingTransactionName
                        );

                        if (loading !== undefined) {
                            extensionThis.storage.loading = loading;
                        }

                        if (matchUpdated) extensionThis.storage.match = match;
                        if (matchRangeUpdated)
                            extensionThis.storage.matchRange = matchRange;

                        const languageToolDecorations = tr.getMeta(
                            LanguageToolHelpingWords.LanguageToolTransactionName
                        );
                        if (languageToolDecorations) return decorationSet;

                        if (tr.docChanged && extensionThis.options.automaticMode) {
                            if (!proofReadInitially) {
                                debouncedProofreadAndDecorate(tr.doc);
                            } else {
                                const {
                                    selection: { from, to },
                                } = tr;
                                let changedNodeWithPos: { node: PMNode; pos: number } | undefined;

                                tr.doc.descendants((node, pos) => {
                                    if (!node.isBlock) return false;
                                    const [nodeFrom, nodeTo] = [pos, pos + node.nodeSize];
                                    if (!(nodeFrom <= from && to <= nodeTo)) return;
                                    changedNodeWithPos = { node, pos };
                                });

                                if (changedNodeWithPos) {
                                    onNodeChanged(
                                        changedNodeWithPos.node,
                                        changedNodeWithPos.node.textContent,
                                        changedNodeWithPos.pos + 1
                                    );
                                }
                            }
                        }

                        decorationSet = decorationSet.map(tr.mapping, tr.doc);
                        setTimeout(addEventListenersToDecorations, 100);
                        return decorationSet;
                    },
                },
                view: () => ({
                    update: (view) => {
                        editorView = view;
                        setTimeout(addEventListenersToDecorations, 100);
                    },
                }),
            }),
        ];
    },
});

export default LanguageTool;

